import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import {
  FormField,
  useFieldIds,
  useFieldConnection,
  type ConnectedFieldProps,
} from "@/shared/ui/core";

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  /** Clases del contenedor del campo (no del `<textarea>`). */
  fieldClassName?: string;
}

/**
 * El campo de texto largo que le faltaba al sistema.
 *
 * `core/Input` resuelve label, pista, error y ARIA para un `<input>`, y
 * la descripción de un producto no cabe en uno. Esto es el mismo trato
 * —mismo `FormField`, mismos ids, mismo lenguaje visual— sobre un
 * `<textarea>`. Candidato claro a `shared/ui/core/`: el primer formulario
 * con un campo de notas lo va a necesitar igual.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      label,
      hint,
      error,
      required,
      fieldClassName,
      className,
      id,
      disabled,
      rows = 4,
      "aria-describedby": extraDescribedBy,
      ...rest
    },
    ref,
  ) {
    const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
      hasHint: Boolean(hint),
      hasError: Boolean(error),
    });

    const describedByAll =
      [describedBy, extraDescribedBy].filter(Boolean).join(" ") || undefined;

    return (
      <FormField
        label={label}
        hint={hint}
        error={error}
        required={required}
        controlId={controlId}
        hintId={hintId}
        errorId={errorId}
        className={fieldClassName}
      >
        <textarea
          ref={ref}
          id={controlId}
          rows={rows}
          required={required}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedByAll}
          className={cn(
            "block w-full rounded-2xl border bg-ds-surface px-4 py-3 text-sm text-ds-ink",
            "outline-none transition placeholder:text-ds-ink-4",
            "focus:border-ds-lilac focus:ring-2 focus:ring-ds-lavender-bg-2/50",
            "disabled:cursor-not-allowed disabled:bg-ds-cream-2 disabled:opacity-60",
            error
              ? "border-ds-terracotta focus:border-ds-terracotta focus:ring-ds-terracotta/20"
              : "border-ds-border",
            className,
          )}
          {...rest}
        />
      </FormField>
    );
  },
);

export interface TextAreaFieldProps
  extends Omit<TextAreaProps, "name">,
    ConnectedFieldProps {}

/** El `TextArea` ya cableado al `<Form>` que lo envuelve. */
export function TextAreaField({
  name,
  registerOptions,
  disabled,
  error,
  ...rest
}: TextAreaFieldProps) {
  const field = useFieldConnection(name, registerOptions);

  return (
    <TextArea
      {...rest}
      disabled={disabled ?? field.disabled}
      error={error ?? field.error}
      {...field.registration}
    />
  );
}
